import { useCallback, useMemo, useRef, useState } from 'react';

import { Input } from 'components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { cn } from 'lib/utils';

import './style.scss';

const PRESET_COLORS = [
  '#000000',
  '#FFFFFF',
  '#F44336',
  '#E91E63',
  '#9C27B0',
  '#673AB7',
  '#3F51B5',
  '#2196F3',
  '#03A9F4',
  '#00BCD4',
  '#009688',
  '#4CAF50',
  '#8BC34A',
  '#CDDC39',
  '#FFEB3B',
  '#FFC107',
  '#FF9800',
  '#FF5722',
  '#795548',
  '#607D8B',
];

const HEX_REGEX = /^#([0-9A-Fa-f]{6})$/;

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const ColorPicker = ({
  value,
  onChange,
  disabled,
  placeholder = 'Enter hex color (e.g. #FF5733)',
}: ColorPickerProps) => {
  const [open, setOpen] = useState(false);
  const nativeInputRef = useRef<HTMLInputElement>(null);

  const isValidHex = useMemo(() => HEX_REGEX.test(value), [value]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.trim();
      onChange(val);
    },
    [onChange],
  );

  const handlePresetClick = useCallback(
    (color: string) => {
      onChange(color);
    },
    [onChange],
  );

  const handleNativeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value.toUpperCase());
    },
    [onChange],
  );

  const displayColor = isValidHex ? value : '#CCCCCC';

  return (
    <div className="color-picker-wrapper">
      <Popover open={open} onOpenChange={setOpen}>
        <div className="color-picker-input-row">
          <PopoverTrigger asChild disabled={disabled}>
            <button
              type="button"
              className={cn('color-picker-swatch', disabled && 'color-picker-swatch--disabled')}
              style={{ backgroundColor: displayColor }}
              aria-label="Pick a color"
            >
              {!isValidHex && !value && <span className="color-picker-swatch-empty" />}
            </button>
          </PopoverTrigger>
          <Input
            type="text"
            className="color-picker-text-input"
            value={value}
            onChange={handleInputChange}
            placeholder={placeholder}
            disabled={disabled}
          />
        </div>

        <PopoverContent
          className="color-picker-popover"
          align="start"
          sideOffset={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="color-picker-presets">
            {PRESET_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={cn(
                  'color-picker-preset',
                  value.toUpperCase() === color && 'color-picker-preset--active',
                )}
                style={{ backgroundColor: color }}
                onClick={() => handlePresetClick(color)}
                aria-label={`Select color ${color}`}
              />
            ))}
          </div>

          <div className="color-picker-custom">
            <button
              type="button"
              className="color-picker-custom-btn"
              onClick={() => nativeInputRef.current?.click()}
            >
              Custom color...
            </button>
            <input
              ref={nativeInputRef}
              type="color"
              className="color-picker-native"
              value={isValidHex ? value : '#000000'}
              onChange={handleNativeChange}
              tabIndex={-1}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ColorPicker;
